"use client";

import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "next-intl";
import { UserInfo } from "./components/user-info";
import { UserAvatar } from "./components/user-avatar";
import { type UserDetailsFragment } from "@/gql/graphql";
import { logout } from "@/app/actions";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";

type Props = {
	user: UserDetailsFragment;
};

/** Headless UI `MenuItem` render-prop (v2: prefer `focus` over deprecated `active`). */
type MenuItemRenderProps = {
	focus: boolean;
	disabled: boolean;
	close: () => void;
};

export function UserMenu({ user }: Props) {
	const t = useTranslations("nav");

	return (
		<Menu as="div" className="relative">
			<MenuButton className="focus:outline-hidden relative flex rounded-full bg-secondary text-sm focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-foreground">
				<span className="sr-only">Open user menu</span>
				<UserAvatar user={user} />
			</MenuButton>
			<MenuItems
				modal={false}
				transition
				anchor="bottom end"
				className={clsx(
					"focus:outline-hidden z-10 min-w-[13rem] origin-top-right divide-y divide-border bg-card py-1 text-start shadow-sm ring-1 ring-border ring-opacity-5",
					"[--anchor-gap:0.5rem] [--anchor-padding:0.5rem]",
					"transform transition ease-out",
					"data-[closed]:scale-95 data-[closed]:opacity-0",
					"data-[enter]:duration-100 data-[leave]:duration-75 data-[leave]:ease-in",
				)}
			>
				<UserInfo user={user} />
				<div className="flex flex-col px-1 py-1">
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/account"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("myAccount")}
							</LinkWithChannel>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/orders"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("myOrders")}
							</LinkWithChannel>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/account/referrals"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("referrals")}
							</LinkWithChannel>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/account/support"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("support")}
							</LinkWithChannel>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/account/addresses"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("addresses")}
							</LinkWithChannel>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<LinkWithChannel
								href="/account/loyalty"
								className={clsx(
									focus && "bg-muted",
									"block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground",
								)}
							>
								{t("studienPoints")}
							</LinkWithChannel>
						)}
					</MenuItem>
				</div>
				<div className="flex flex-col px-1 py-1">
					<MenuItem>
						{({ focus }: MenuItemRenderProps) => (
							<form action={logout}>
								<button
									type="submit"
									className={clsx(
										focus && "bg-muted",
										"block w-full px-4 py-2 text-start text-sm font-medium text-muted-foreground hover:text-foreground",
									)}
								>
									Log Out
								</button>
							</form>
						)}
					</MenuItem>
				</div>
			</MenuItems>
		</Menu>
	);
}
